import * as React from "react";
import { format } from "date-fns";
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon } from "lucide-react";

import { cn } from "../../lib/utils";
import { useUiDateLocale } from "../../lib/ui-date-locale";
import { Button } from "./button";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

type MonthPickerProps = {
  id?: string;
  /** First day of the selected month (only year + month are meaningful). */
  value?: Date | null;
  onChange?: (date?: Date) => void;
  onBlur?: React.FocusEventHandler<HTMLButtonElement>;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  "aria-describedby"?: React.AriaAttributes["aria-describedby"];
  "aria-invalid"?: React.AriaAttributes["aria-invalid"];
  "aria-label"?: React.AriaAttributes["aria-label"];
};

const MONTHS = Array.from({ length: 12 }, (_, index) => index);

/** Select a calendar month (year + month only) — clearer than a day picker when only the month matters. */
export function MonthPicker({
  id,
  value,
  onChange,
  onBlur,
  placeholder = "Pick a month",
  className,
  disabled,
  "aria-describedby": ariaDescribedBy,
  "aria-invalid": ariaInvalid,
  "aria-label": ariaLabel,
}: MonthPickerProps) {
  const locale = useUiDateLocale();
  const [open, setOpen] = React.useState(false);
  const [year, setYear] = React.useState(() => (value ?? new Date()).getFullYear());

  React.useEffect(() => {
    if (value) {
      setYear(value.getFullYear());
    }
  }, [value]);

  const selectedYear = value?.getFullYear();
  const selectedMonth = value?.getMonth();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          data-empty={!value}
          disabled={disabled}
          onBlur={onBlur}
          aria-describedby={ariaDescribedBy}
          aria-invalid={ariaInvalid}
          aria-label={ariaLabel}
          className={cn(
            "data-[empty=true]:text-muted-foreground w-[280px] justify-start text-left font-normal capitalize",
            className,
          )}
        >
          <CalendarIcon />
          {value ? format(value, "LLLL yyyy", { locale }) : <span className="normal-case">{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3" align="start">
        <div className="mb-3 flex items-center justify-between">
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={() => setYear((current) => current - 1)}
            aria-label="Previous year"
          >
            <ChevronLeftIcon className="size-4" />
          </Button>
          <span className="text-sm font-medium tabular-nums">{year}</span>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={() => setYear((current) => current + 1)}
            aria-label="Next year"
          >
            <ChevronRightIcon className="size-4" />
          </Button>
        </div>
        <div className="grid grid-cols-3 gap-1.5">
          {MONTHS.map((month) => {
            const isSelected = selectedYear === year && selectedMonth === month;
            return (
              <Button
                key={month}
                type="button"
                variant={isSelected ? "default" : "ghost"}
                size="sm"
                className="capitalize"
                onClick={() => {
                  onChange?.(new Date(year, month, 1));
                  setOpen(false);
                }}
              >
                {format(new Date(year, month, 1), "LLL", { locale })}
              </Button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
